/**
 * Singleton-обёртка над одним HTMLAudioElement.
 *
 * - Audio-элемент создаётся лениво при первом обращении.
 *   Это важно для prerender-сборки и серверного контекста, где `Audio`
 *   и `localStorage` не определены.
 * - Один audio-элемент для всех станций. При переключении станции
 *   меняем `src` и вызываем `load()`.
 * - Для HLS-потоков (.m3u8) hls.js подгружается динамически и только
 *   тогда, когда пользователь действительно нажал Play на такую станцию.
 *   Safari играет HLS нативно — hls.js там не грузится.
 */

import type Hls from 'hls.js';

let audio: HTMLAudioElement | null = null;
let currentSrcUrl = '';
let currentHls: Hls | null = null;

const isHlsUrl = (url: string) => /\.m3u8($|\?)/i.test(url);

export function getAudioEl(): HTMLAudioElement {
  if (audio) return audio;
  if (typeof Audio === 'undefined') {
    throw new Error('Audio API недоступен (вызов вне браузера).');
  }
  const el = new Audio();
  // preload="none" — поток не запрашивается, пока не нажали Play.
  el.preload = 'none';
  // НЕ выставляем crossOrigin: многие icecast/shoutcast серверы
  // не отдают CORS-заголовки. С crossOrigin='anonymous' воспроизведение
  // блокируется. Для простого playback'а это поле не требуется.
  audio = el;
  return el;
}

export function hasAudio(): boolean {
  return typeof Audio !== 'undefined';
}

function detachHls() {
  if (currentHls) {
    try {
      currentHls.destroy();
    } catch {
      /* ignore */
    }
    currentHls = null;
  }
}

export async function loadStream(url: string): Promise<void> {
  const el = getAudioEl();
  if (currentSrcUrl === url && el.src) return;

  detachHls();

  if (isHlsUrl(url)) {
    const canPlayNative = el.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (canPlayNative) {
      el.src = url;
      el.load();
    } else {
      const HlsCtor = (await import('hls.js')).default;
      if (HlsCtor.isSupported()) {
        const hls = new HlsCtor({
          enableWorker: false,
          lowLatencyMode: false,
        });
        hls.loadSource(url);
        hls.attachMedia(el);
        currentHls = hls;
      } else {
        // финальный fallback — поручаем браузеру, может справится
        el.src = url;
        el.load();
      }
    }
  } else {
    el.src = url;
    el.load();
  }

  currentSrcUrl = url;
}

export async function playAudio(): Promise<void> {
  const el = getAudioEl();
  await el.play();
}

export function pauseAudio() {
  if (!audio) return;
  audio.pause();
}

export function stopAudio() {
  detachHls();
  if (!audio) return;
  audio.pause();
  audio.removeAttribute('src');
  try {
    audio.load();
  } catch {
    /* ignore */
  }
  currentSrcUrl = '';
}

export function setVolume(v: number) {
  const el = getAudioEl();
  el.volume = Math.max(0, Math.min(1, v));
}

export function setMutedState(m: boolean) {
  const el = getAudioEl();
  el.muted = m;
}

export function getCurrentStreamUrl(): string {
  return currentSrcUrl;
}

export function onAudioEvent(
  event: keyof HTMLMediaElementEventMap,
  handler: (e: Event) => void,
): () => void {
  const el = getAudioEl();
  el.addEventListener(event, handler);
  return () => el.removeEventListener(event, handler);
}
